import React, { useState, useEffect } from 'react';
import { FloatingInput, FloatingDropdown, Button } from '../../../components/ui';
import { X } from 'lucide-react';
import { useAddComplaintType, useUpdateComplaintType } from '../hooks/useComplaintTypeMutations';
import type { ComplaintType } from '../../../services/complaintTypeApi';
import { COMPLAINT_TYPE_STATUS, STATUS_OPTIONS } from '../config/constants';

interface ComplaintTypeModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	editingItem: ComplaintType | null;
}

export const ComplaintTypeModal: React.FC<ComplaintTypeModalProps> = ({
	open,
	onClose,
	onSuccess,
	editingItem,
}) => {
	const [name, setName] = useState('');
	const [status, setStatus] = useState<string>(COMPLAINT_TYPE_STATUS.ACTIVE);
	const [error, setError] = useState<string | null>(null);

	// Use React Query mutations with optimistic updates
	const addMutation = useAddComplaintType();
	const updateMutation = useUpdateComplaintType();
	const isSubmitting = addMutation.isPending || updateMutation.isPending;

	useEffect(() => {
		if (open) {
			if (editingItem) {
				setName(editingItem.name || '');
				// API returns status as string ('Active' or 'Inactive')
				setStatus(editingItem.status === 'Active' ? COMPLAINT_TYPE_STATUS.ACTIVE : COMPLAINT_TYPE_STATUS.INACTIVE);
			} else {
				setName('');
				setStatus(COMPLAINT_TYPE_STATUS.ACTIVE);
			}
			setError(null);
		}
	}, [open, editingItem]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!name.trim()) {
			setError('QC Type name is required');
			return;
		}

		try {
			if (editingItem) {
				// Update existing item with optimistic update
				const statusString = status === COMPLAINT_TYPE_STATUS.ACTIVE ? 'Active' : 'Inactive';
				await updateMutation.mutateAsync({
					id: editingItem.id,
					name: name.trim(),
					status: statusString,
				});
				onSuccess();
			} else {
				// Add new item with optimistic update
				await addMutation.mutateAsync({
					name: name.trim(),
				});
				onSuccess();
			}
		} catch (err: unknown) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to save QC type';
			setError(errorMessage);
			console.error('Error saving QC type:', err);
		}
	};

	if (!open) return null;

	return (
		<div
			className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'
			onClick={onClose}
		>
			<div
				className='bg-white rounded-lg p-6 max-w-md w-full mx-4'
				onClick={e => e.stopPropagation()}
			>
				<div className='flex items-center justify-between mb-6'>
					<h3 className='text-xl font-semibold text-gray-900'>
						{editingItem ? 'Edit QC Type' : 'Add QC Type'}
					</h3>
					<button onClick={onClose} className='p-1 hover:bg-gray-100 rounded transition-colors'>
						<X className='w-5 h-5 text-gray-500' />
					</button>
				</div>

				<form onSubmit={handleSubmit} className='space-y-4'>
					<FloatingInput
						label='QC Type Name'
						value={name}
						onChange={setName}
						required
						error={!!(error && !name.trim())}
						errorMessage={error && !name.trim() ? error : undefined}
					/>

					{editingItem && (
						<FloatingDropdown
							label='Status'
							options={STATUS_OPTIONS.filter(opt => opt.value !== COMPLAINT_TYPE_STATUS.ALL)}
							value={status}
							onChange={setStatus}
							required
						/>
					)}

					{error && error.includes('required') === false && (
						<div className='text-red-600 text-sm'>{error}</div>
					)}

					<div className='flex justify-end space-x-3 mt-6'>
						<Button type='button' variant='outline' onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button type='submit' disabled={isSubmitting} variant='primary'>
							{isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Add'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};
